import {
  FC,
  useContext,
  useEffect,
  useState,
  ChangeEvent,
  useRef,
} from "react";
import { useModalAction } from "../../components/modal-views/context";
import client from "../../api";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  getCurrencyInfo,
  getErrorMessage,
  getStatusStyle,
  logger,
  makeNumberWithFloatingDigits,
} from "../../lib/utils";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { Form, InputNumber, Table } from "antd";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  SUCCESS_MODAL_TYPE,
} from "src/lib/constants";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import useAuth from "src/hooks/use-auth";
import { AdminUser } from "src/api/types";
import { PermissionDataType } from "src/lib/constants/table-type";
import { ColumnsType } from "antd/es/table";
import { callback } from "chart.js/dist/helpers/helpers.core";
import ToggleSwitch from "src/components/ui/switch";
import Input from "src/components/ui/input";

const schema = yup
  .object({
    full_name: yup
      .string()
      .trim()
      .matches(
        /^[a-zA-Z]+( [a-zA-Z]+)+$/,
        "Full name must be at least two words, do not contain numbers and special symbol!"
      )
      .max(60, "Full name max length must be not more 60 characters!")
      .required("Please, enter your full name!"),
    email: yup
      .string()
      .required("This field is required.")
      .matches(
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        "Please write a valid email address."
      ),
  })
  .required();
type FormData = yup.InferType<typeof schema>;

type ProfilePageProps = {
  isNew?: boolean;
  editingUser?: AdminUser;
  onCallback?: any;
};

const ProfilePage: FC<ProfilePageProps> = ({
  isNew,
  editingUser,
  onCallback,
}) => {
  const { isAuthorized, getCurrrentUser, setCurrentUser } = useAuth();
  const currentUser = getCurrrentUser() || {};
  const {
    first_name = "",
    last_name = "",
    password = "",
    email = "",
    google2fa_enabled = 0,
    permissions = [],
  } = currentUser;

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal, closeModal } = useModalAction();

  const [dataSource, setDataSource] = useState<PermissionDataType[]>([]);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  dataSource.sort((a, b) => {
    if (a.id === 12 || a.id === 13) {
      return -1;
    }
    if (a.id === 13 || b.id === 12) {
      return 1;
    }
    return 0;
  });

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    reset,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues: {
      full_name: `${first_name} ${last_name}`.trim(),
      email: email,
    },
  });

  useEffect(() => {
    const fetchAdminProfile = async () => {
      const user = getCurrrentUser();
      if (!user) return;

      setIsProfileLoading(true);
      try {
        const resData = await client.auth.getProfile();
        const rawData = resData?.data || resData;
        if (rawData) {
          const rawPermissions =
            rawData?.role?.permissions || rawData?.permissions || [];
          const normalizedPermissions = rawPermissions.map((p: any) => ({
            id: p.permission_id ?? p.id,
            name: p.permission_name ?? p.name,
            permission_id: p.permission_id ?? p.id,
            permission_name: p.permission_name ?? p.name,
            permission_description: p.permission_description ?? null,
            can_add: Boolean(p.can_add),
            can_edit: Boolean(p.can_edit),
            can_list: Boolean(p.can_list),
            can_view: Boolean(p.can_view),
            can_delete: Boolean(p.can_delete),
            can_approve_reject: Boolean(p.can_approve_reject),
            can_export: Boolean(p.can_export),
            can_download: Boolean(p.can_download),
            can_block: Boolean(p.can_block),
          }));

          const normalizedUserData: AdminUser = {
            ...user,
            ...rawData,
            id: rawData?.id ?? user?.id,
            email: rawData?.email ?? user?.email,
            first_name: rawData?.first_name ?? user?.first_name,
            last_name: rawData?.last_name ?? user?.last_name,
            google2fa_enabled: rawData?.google2fa_enabled ? 1 : 0,
            last_login: rawData?.last_login ?? user?.last_login,
            is_active: rawData?.is_active ?? user?.is_active,
            last_activity: rawData?.last_activity ?? user?.last_activity,
            role_id: rawData?.role_id ?? user?.role_id,
            is_different_channel_access: Boolean(
              rawData?.is_different_channel_access ??
                user?.is_different_channel_access
            ),
            channel_partner_id:
              rawData?.channel_partner_id ?? user?.channel_partner_id,
            created_at: rawData?.created_at ?? user?.created_at,
            updated_at: rawData?.updated_at ?? user?.updated_at,
            token: user?.token,
            role: rawData?.role
              ? {
                  ...rawData.role,
                  permissions: normalizedPermissions,
                }
              : user?.role,
            permissions:
              normalizedPermissions.length > 0
                ? normalizedPermissions
                : user?.permissions,
          };

          setCurrentUser(normalizedUserData, user?.password);

          const fullNameVal = `${normalizedUserData.first_name || ""} ${
            normalizedUserData.last_name || ""
          }`.trim();
          setValue("full_name", fullNameVal);
          setValue("email", normalizedUserData.email || "");
        }
      } catch (err: any) {
        console.error("Failed to fetch admin profile", err);
      } finally {
        setIsProfileLoading(false);
      }
    };

    fetchAdminProfile();
  }, []);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 640 ? true : false);
  }


  const onValueChanged = (type: string, value: boolean) => {
    logger.log(type, value);
    const newData = [...dataSource];
    const index = newData.findIndex((item) => type == item.name);
    if (index > -1) {
      const item = newData[index];
      const editingData = { ...item, value };
      newData.splice(index, 1, {
        ...item,
        ...editingData,
      });
      setDataSource(newData);
    }
  };

  const onCancel = () => {
    onCallback(false);
  };

  const columns: ColumnsType<PermissionDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action name
        </p>
      ),
      dataIndex: "name",
      key: "name",
      className: "w-auto",
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {capitalizeFirstLetter(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          On/Off
        </p>
      ),
      dataIndex: "value",
      key: "value",
      className: "w-[80px]",
      render: (_, record: { key: React.Key; name: string; value: boolean }) => (
        <div className="flex flex-row gap-2 items-center">
          <ToggleSwitch
            type={record.name}
            value={record.value}
            onValueChanged={onValueChanged}
          />
        </div>
      ),
    },
  ];


  return (
    <form className="w-full h-full flex-col p-2 sm:p-4">
      <div className="w-full flex flex-row items-center justify-between">
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
          Admin
        </p>
      </div>

      <div className="flex flex-col md:flex-row mt-2 md:mt-4 gap-2 md:gap-4">
        <div className="flex flex-col gap-4 md:w-[1080px]">
          <div className="bg-white rounded-[12px] p-4 w-full ">
            <Input
              label="Full name"
              placeholder="Enter full name"
              className="w-full mt-4"
              disabled={true}
              {...register("full_name")}
              error={errors.full_name && (errors.full_name.message as string)}
            />
            <Input
              label="E-mail"
              placeholder="Enter e-mail"
              id="new-email"
              autoComplete="new-email"
              className="w-full mt-4"
              disabled={true}
              {...register("email")}
              error={errors.email && (errors.email.message as string)}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default ProfilePage;
